import type { AxiosError } from 'axios';
import type { Color, IconName, Table3Ref, TableFilter, useTableDataLoaderValues } from '@economic/taco';
import type { ProfileDto } from '~/types/internal-api';
import type { StepperConfig } from './cards/Dialog/OrderCard/config';
import type { InboxDocument } from '../../modules/inbox/types';
import { ChartType, PeriodUnit } from './common/generic-chart-card/types';

export enum UserType {
    Normal = 1,
    MobileExpense = 2,
}

export interface CardFilter extends TableFilter {
    id: keyof CardResponse;
}

export interface TransactionFilter extends TableFilter {
    id: keyof Transaction;
}

export interface CardResponse {
    activeStatus: ActiveStatus;
    allowAtm: boolean;
    allowEcomm: boolean;
    amountSpentValue: number;
    cardStatus: CardStatus;
    companyId: string;
    expirationDateMonth: number;
    expirationDateYear: number;
    externalId?: string;
    id: string;
    label: string;
    lastFourDigits: string;
    localId: number;
    monthlyLimit: number | null;
    type: CardType;
    userId: string;
    createdAt: string;
    cardHolderName: string;
    orderedAt: string; // datetime
    orderedAtDate: string; // date for the filter

    // status changes information, exist only if it was changed
    statusUpdateByFullName?: string;
    statusUpdateAt?: string;
    statusUpdateBy?: string; // user id
}

export enum ActiveStatus {
    UNKNOWN = 0,
    NOT_ACTIVE = 1,
    ACTIVE = 2,
    ACTIVE_ABOUT_TO_EXPIRE = 3,
}

export enum CardType {
    UNKNOWN = 0,
    PHYSICAL = 1,
    VIRTUAL = 2,
}

export enum CardStatus { // in UI it is:
    UNKNOWN = 0, // "unknown"
    OK = 1, // "active"
    PRE_ORDER = 2, // "inactive"
    FROZEN = 3, // "blocked"
    BLOCKED = 4, // "blocked"
    TERMINATED = 5, // "terminated"

    // not used in UI but for the filter we used it
    INACTIVE = 6,
}

export interface PersonResponse {
    id: string;
    personalId?: PersonalId;
    role: Role;
    name: string;
    email: string;
    phoneNumber?: string;
    externalId: string;
    invitationSentTimestamp?: string;
}

export interface PersonalId {
    identifier?: string;
    country?: string;
}

export enum Role {
    ADMIN = 'ADMIN',
    ACCOUNTANT = 'ACCOUNTANT',
    CARD_HOLDER = 'CARD_HOLDER',
    NONE = 'NONE',
}

export interface TransactionCategories {
    id: string;
    rev: number;
    name: string;
    accountNo: string;
    taxRate: number;
    icon: 'HOME' | 'COMPUTER' | 'OTHER' | 'HEART' | 'PACKAGE';
    color: string; // ex.'YELLOW' | 'PURPLE'
    dimensionIds: unknown[];
    reverseVat: boolean;
    requireParticipants: boolean;
    hasVat: boolean;
}

export interface TransactionAmount {
    value: number;
    currency: string;
}

export interface ControlComponentProps<T = unknown> {
    setValue: (value: T) => void;
    value: T;
}

export enum TransactionStatus {
    SENT = 'SENT',
    NOT_SENT = 'NOT_SENT',
}

export interface Transaction {
    id: string;
    accountNumber: number;
    localId: number;
    documentNumber?: number;
    status: string;
    personExternalId: string;
    personName: string;
    cardId: string;
    date: string;
    transactionDate: string;
    settlementDate: string;
    totalAmountValue: number;
    totalAmountCurrency: string;
    originalAmountValue?: number;
    originalAmountCurrency?: string;
    name: string;
    city: string;
    country: string;
    responseCode: string;
    responseCodeDescription: string;
    note: string;
    lastFourDigits: string;
    hasDocument: boolean;
    draftEntryLogId?: number;
    draftEntryLogCreatedAt?: string;
    // in case if not a normal purchase/deposit then it is from mynt and can have the following labels:
    // "ADJUSTMENT" ,"CASHBACK", "CREDIT_FEE", "CREDIT_NOTE", "DEPOSIT", "PENALTY", "SERVICE_FEE", "WITHDRAWAL"
    type: string;
}

export interface Country {
    name: string;
    dialCode: string;
    code: string;
    currency: string;
}

export interface TransactionTotals {
    currency: string;
    year: number;
    month: number;
    total: number;
}

export interface TransactionOld {
    id: string;
    personId: string;
    transactionDate: string;
    settlementDate: string;
    amount: {
        value: number;
        currency: string; // "SEK"
    };
    originalAmount: unknown;
    description: string;
    isSettled: boolean;
    hasReceipt: boolean;
    hasAccounting: boolean;
    isTopUp: boolean;
    accounting: {
        categoryId: unknown;
        categoryName: string;
        accountNo: unknown;
        taxRate: number;
        taxAmount: {
            value: number;
            currency: string; // SEK
        };
        hasVat: boolean;
        requireParticipants: boolean;
        participants: unknown[];
        voucher: unknown;
        reverseVat: boolean;
        failedExportMessage: unknown;
    };
    comment: string | null;
    rejectReason: unknown;
    rejectedBy: string | null;
    dimensionValues: unknown[];
    status: string; // NEW
    personName: string;
    cardDetailsDto: unknown;
}

export type PaymentCardsWalletTab = 'overview' | 'accountsettings' | 'topup' | 'credit' | 'categories';

export interface CommonContextValues<State, Entity> {
    state: State;
    tableProps: useTableDataLoaderValues<Entity>;
    tableRef: React.RefObject<Table3Ref>;

    tableRefetch: () => void;
    setState: (key: keyof State, value: State[keyof State]) => void;
    setStates: (state: Partial<State>) => void;
}

export interface PaymentCardsSettings {
    general: {
        journal: string;
        expenseAccount?: number;
        contraAccount?: number;
        expenseAccountValidation: boolean;
    };
    topup: {
        minBalanceAmount: number;
        addAmount: number;
        autoTopUp: boolean;
    };
    wallet: {
        showTasks: boolean;
        chartTypes: Record<OverviewChartEnum, ChartType>;
    };
}

export interface PaymentCardsWalletUserSettings {
    accountNumber: string;
    fullName: string;
    recipient: string;
    registrationNumber: string;
}

export type GenericAxiosError = AxiosError<{ reason?: string; message?: string }>;

export interface PaymentCardsSettingsContextValue {
    settings: PaymentCardsSettings;
    isLoading: boolean;
    updateSettings: (key: keyof PaymentCardsSettings, value: PaymentCardsSettings[keyof PaymentCardsSettings]) => Promise<void>;
    refetch: () => Promise<void>;
}

export interface PaymentCardsWalletState {
    cards: CardResponse[] | undefined;
    tab: PaymentCardsWalletTab;
    isLoading: boolean;
    account: PaymentCardAccountResponse;
    transferInfo: MyntAccountInformation;
    autoTopUp: AutoTopUpResponse;
}

export type PaymentCardsCardsDialogType = 'OrderCard' | 'ActivateCard';

export interface PaymentCardsWalletContextValues {
    state: PaymentCardsWalletTab;
    setTab: (tabId: PaymentCardsWalletTab) => void;
    transferInfo: {
        receiver: string;
        registrationNumber: string;
        accountNumber: string;
    };
}

export enum PaymentCardsDrawerContentEnum {
    Menu = 'Menu',
    Details = 'Details',
    Settings = 'Settings',
}

export interface PaymentCardsDrawerSettingsState {
    cardHolder: string;
    cardLabel: string;
    cardLimit: number | null;
}

export enum SensitiveDataOperationEnum {
    Pin,
    Details,
}

export interface PaymentCardsState {
    currentCard: CardResponse | null;
    selectedCards: string[];
    isLoading: boolean;
    showOrderCardDialog: boolean;
    showActivateCardDialog: boolean;
}

export interface PaymentCardsCardPinInfo {
    controlId?: string;
    url: string;
    timestamp: number;
}

export interface PaymentCardsCardDetailsInfo {
    token?: string;
    url: string;
    timestamp: number;
}

export type PaymentCardSecurityOperation = 'view-pan' | 'view-pin';

interface LegacyDocumentUploadResponseFile {
    PageNumber: number;
    expiringImageUrl: null | string;
    expiringThumbUrl: string | null;
}

export interface LegacyDocumentUploadResponse {
    CreationDate: string;
    ElectronicInvoiceUrl: string | null;
    Files: LegacyDocumentUploadResponseFile[];
    Id: number;
    Info: null | string;
    IsAttachedToBookedJournalEntry: boolean;
    IsPdf: boolean;
    Note: null | string;
    NumberOfPages: number;
    OwnerJournalEntries: unknown[];
    PictureNumber: number;
    Revision: string;
    Status: number;
    VoucherNumber: string | null;
    expiringPdfUrl: string | null;
}

export interface TransactionTableUploadData {
    localId: number;
    documentNumber?: number;
}

export interface PaymentCardsCardsDrawerState {
    contentId: PaymentCardsDrawerContentEnum;
    card: CardResponse | null;
    isLoading: boolean;
    cardPin: PaymentCardsCardPinInfo;
    cardDetails: PaymentCardsCardDetailsInfo;
}

export interface PaymentCardsTransactionsDrawerFormState {
    date: string;
    amount: number;
    amountWithCurrency: string;
    originalAmountWithCurrency?: string;
    currency: string;
    supplier: string;
    employee: string;
    note: string;
    accountNumber?: number;
}

export interface PaymentCardsTransactionsDrawerFormHandlerData {
    dirty: boolean;
    valid: boolean;
    values: PaymentCardsTransactionsDrawerFormState;
}

export interface PaymentCardsTransactionsDrawerState {
    transaction: Transaction | null;
    document: InboxDocument | null;
    isLoading: boolean;
    isDocumentLoading: boolean;
    pageIndex: number;
    pageCount: number;
    isUpdating: boolean;
    formStateValues?: PaymentCardsTransactionsDrawerFormState;
}

export interface PaymentCardsTransactionsState {
    transaction: Transaction | null;
    selectedTransactions: Transaction[];
    isLoading: boolean;
    uploadReceipts: number[];
}

export enum OrderCardStepEnum {
    UserDetails = 0,
    SelectCard = 1,
    OrderSummary = 2,
}

export interface User extends ProfileDto {
    guid: string;
}

export interface PaymentCardsOrderAddress {
    country: string;
    address: string;
    address2: string;
    city: string;
    postalCode: string;
}

interface PaymentCardsOrderCardButtonProps {
    appearance: 'default' | 'primary';
    label: string;
    isVisible: boolean;
    isDisabled: boolean;
}

export interface UserFormState {
    userId: string;
    email: string;
    firstName: string;
    lastName: string;
    type: unknown;
}

export interface PaymentCardsOrderCardState {
    dialogTitle: string;
    showStepper: boolean;
    isLoading: boolean;
    step: OrderCardStepEnum;
    subStep: number;

    user: User | null;
    userCards: CardResponse[];
    users: User[];
    countries: Country[];

    cardType: CardType;
    email: string;
    phoneNumber: string;
    cardLabel: string;
    monthlySpendingLimit?: number;

    userForm: UserFormState;
    addressForm: PaymentCardsOrderAddress;
}

export interface PaymentCardsOrderCardValidationData {
    state: PaymentCardsOrderCardState;
}

export interface PaymentCardsOrderCardContextValues {
    actions: {
        prev: PaymentCardsOrderCardButtonProps;
        next: PaymentCardsOrderCardButtonProps;
    };
    state: PaymentCardsOrderCardState;
    stepperConfig: StepperConfig;

    setState: (
        key: keyof PaymentCardsOrderCardState,
        value: PaymentCardsOrderCardState[keyof PaymentCardsOrderCardState]
    ) => void;
    setMultipleState: (newState: Partial<PaymentCardsOrderCardState>) => void;

    onChangeFormState: (ev: React.ChangeEvent<HTMLInputElement>) => void;
    onAutoCompleteAddressWithCompanyData: () => void;

    onSetCardType: (cardType: CardType) => void;
    onSelectUser: (user: User) => void;

    // navigations
    onContinue: () => void;
    onJumpToTheCreateStep: () => void;
    onBack: () => void;
}

export interface KYCInformation {
    kycUrl: string;
    kycShouldBePerformed: boolean;
    kycStatus: string;
}

export interface CompanyResponseModel {
    enableCustomerAnonymization: boolean;
    country?: string;
    hasNemHandel: boolean;
    enforce2FA: boolean;
    toBeInvoiced: boolean;
    isTrial: boolean;
    name: string;
    address1: string;
    address2?: string;
    zipCode: string;
    city: string;
    phone: string;
    fax: string;
    contact: string;
    email: string;
    homepage: string;
    invoiceToEmail: string;
    latestInvoiceDate: string;
    cvr: string;
    subscriptionPlanId: number;
    canCompanyToggleModulesSuperUser: boolean;
}

export interface CompanyResponse {
    model: CompanyResponseModel;
    enforce2FAReason: TwoFactorAuthAgreementEnforcementReason;
    shouldDisable2FA: boolean;
    canChangeCustomerAnonymization: boolean;
    skipCVRValidation: boolean;
    isAdministratorPayingForCurrentAgreement: boolean;
}

export interface CollectionPagination {
    count: number;
    pageSize: number;
    returnAll: boolean;
    skipPages: number;
}

export interface Table3ListResponse<T> {
    data: T[];
    pagination: CollectionPagination;
}

export interface DeliveryAddress {
    address1: string;
    address2: string;
    areaCode: string;
    city: string;
    country: string;
}

export interface ResponseErrorContent {
    message: string;
    reason: string;
}

export type ErrorResponse = AxiosError<ResponseErrorContent>;

export interface MyntAccountInformation {
    iban: string;
    bic: string;
    reference: string;
    regNo: string;
    bankAccount: string;
}

export enum AutoTopUpStatusEnum {
    Active = 'active',
    Inactive = 'inactive',
    Pending = 'pending',
    Paused = 'paused',
}

export interface AutoTopUpResponse {
    status: AutoTopUpStatusEnum;
    active: boolean;
    approved: boolean;
    thresholdValue: number;
    thresholdCurrency: string;
    valueValue: number;
    valueCurrency: string;
}

export enum OverviewChartEnum {
    Core = 'payment-cards-wallet-overview1',
}

export type MyntCreditStatus = 'INACTIVE' | 'APPROVED' | 'DENIED';

export interface PaymentCardAccountResponse {
    accountId: string;
    companyId: string;
    availableAmountValue: number;
    availableAmountCurrency: string;
    accountBalanceValue: number;
    accountBalanceCurrency: string;
    creditAmountValue: number;
    creditAmountCurrency: string;
    isCreditPendingReview: boolean;
    creditApplicationStatus: MyntCreditStatus;
}

export interface PaymentCardOVerviewChartData {
    fromDate: string;
    toDate: string;
    unit: PeriodUnit;
    transactions: PaymentCardTransactionsByCardDto[];
}

export interface PaymentCardTransactionsByCardDto {
    userName: string;
    cardHolder: string;
    cardId: string;
    label: string;
    lastFourDigits: string;
    transactionsCount: number;
    totalAmountValue: number;
    totalAmountCurrency: string;
    originalAmountValue: number;
    originalAmountCurrency: string;
    intervalNumber: number;
    fromTransactionDate: string;
    toTransactionDate: string;
}

export interface PaymentCardTodoData {
    title: string;
    description: string;
    link: string;
    icon: IconName;
    color: Color;
}

export enum PaymentCardTodoStatus {
    IN_PROGRESS = 'InProgress',
    COMPLETED = 'Completed',
    NOT_STARTED = 'NotStarted',
}

export interface PaymentCardTodo {
    data: PaymentCardTodoData;
    isDisabled: boolean;
    status: PaymentCardTodoStatus;
    errorMessage: string;
}

export enum TwoFactorAuthAgreementEnforcementReason {
    EnforcedOnNewlyCreatedAgreement = 1,
    EnforcedByRegularUser = 2,
    EnforcedByAdminUser = 3,
    EnforcedOnRolledOutSegment = 4,
}
